import type { ApplePayIntervalUnit, OfferDetails, RawDomOffer } from '../_models';

export const REQUIRED_DOM_KEYS = [
  'description',
  'promoLabel',
  'promoAmount',
  'promoIntervalUnit',
  'promoIntervalCount',
  'regularLabel',
  'regularAmount',
  'regularIntervalUnit',
  'regularIntervalCount',
] as const satisfies readonly (keyof RawDomOffer)[];

export const missingDomKeys = (ds: DOMStringMap): string[] => {
  return REQUIRED_DOM_KEYS.filter((k) => !ds[k]);
}

export const isDomOffer = (ds: DOMStringMap): ds is DOMStringMap & RawDomOffer => {
  return missingDomKeys(ds).length === 0;
}

type ParsedInterval = { unit: ApplePayIntervalUnit; count: number };
const APPLE_UNITS: readonly string[] = ['minute', 'hour', 'day', 'month', 'year'];

export const toApplePayInterval = (rawUnit?: string, rawCount?: string): ParsedInterval | null => {
  const count = parseInt(rawCount ?? '', 10);
  if (!Number.isFinite(count) || count <= 0) return null;
  const unit = (rawUnit ?? '').trim().toLowerCase().replace(/s$/, '');
  if (unit === 'week') return { unit: 'day', count: count * 7 };
  return APPLE_UNITS.includes(unit) ? { unit: unit as ApplePayIntervalUnit, count } : null;
}

const readDomInterval = (attrUnit: string, attrCount: string): ParsedInterval | null => {
  return toApplePayInterval(attrCount, attrUnit) ?? toApplePayInterval(attrUnit, attrCount);
}

const toAmount = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : parseFloat(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n.toFixed(2) : null;
};

export interface NavigaOffer {
  PromotionalTermUnit?: string;
  PromotionalTermLength?: string | number;
  BillingSystemCode5?: string;
  BillingSystemCode7?: string | number;
  Marketing?: {
    MarketingConfirmationName?: string;
    MarketingPrice?: string;
    MarketingTitle?: string;
  };
  ProductPriceGroups?: {
    SolConProductPrice?: string | number;
    AppleRenewPrice?: string | number;
  }[];
}

const DEFAULT_BILLING_LABEL = 'Standard Digital Access';

export const buildOfferFromNaviga = (
  offer: NavigaOffer,
  offerId: number,
  offerGroupId: number,
  terms: string,
): OfferDetails | null => {
  // Naviga can send units like "Weeks" (uppercase/plural); toApplePayInterval normalizes these
  const promo = toApplePayInterval(offer.PromotionalTermUnit, String(offer.PromotionalTermLength ?? ''));
  const regular = toApplePayInterval(offer.BillingSystemCode5, String(offer.BillingSystemCode7 ?? ''));
  const prices = offer.ProductPriceGroups?.[0];
  const promoAmount = toAmount(prices?.SolConProductPrice);
  const regularAmount = toAmount(prices?.AppleRenewPrice);
  if (!promo || !regular || !promoAmount || !regularAmount) return null;

  return {
    billingAgreement: terms,
    offerGroupId,
    offerId,
    promoDuration: `${promo.count} ${promo.unit}`,
    paymentDescription: offer.Marketing?.MarketingConfirmationName ?? '',
    trialIsRecurring: false,
    promo: {
      label: DEFAULT_BILLING_LABEL,
      amount: promoAmount,
      paymentTiming: 'recurring',
      recurringPaymentIntervalUnit: promo.unit,
      recurringPaymentIntervalCount: promo.count,
    },
    regularBilling: {
      label: DEFAULT_BILLING_LABEL,
      amount: regularAmount,
      paymentTiming: 'recurring',
      recurringPaymentIntervalUnit: regular.unit,
      recurringPaymentIntervalCount: regular.count,
      type: 'final',
    },
  };
};

export const buildOfferFromDom = (dataset: RawDomOffer, offerId: number, offerGroup: number, terms: string): OfferDetails | null => {
  const promo = readDomInterval(dataset.promoIntervalUnit, dataset.promoIntervalCount);
  const regular = readDomInterval(dataset.regularIntervalUnit, dataset.regularIntervalCount);
  const promoAmount = toAmount(dataset.promoAmount);
  const regularAmount = toAmount(dataset.regularAmount);
  if (!promo || !regular || !promoAmount || !regularAmount) return null;

  return {
    billingAgreement: terms,
    offerGroupId: Number(offerGroup),
    offerId: Number(offerId),
    promoDuration: `${promo.count} ${promo.unit}`,
    paymentDescription: dataset.description,
    trialIsRecurring: false,
    promo: {
      label: dataset.promoLabel,
      amount: promoAmount,
      paymentTiming: 'recurring',
      recurringPaymentIntervalUnit: promo.unit,
      recurringPaymentIntervalCount: promo.count,
    },
    regularBilling: {
      label: dataset.regularLabel,
      amount: regularAmount,
      paymentTiming: 'recurring',
      recurringPaymentIntervalUnit: regular.unit,
      recurringPaymentIntervalCount: regular.count,
      type: 'final',
    },
  };
}